import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  LikeOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { DAYS, HOURS, MINUTES, SECONDS } from "@p0security/shared/time";
import {
  ApprovalDetails,
  PermissionRequest,
} from "@p0security/shared/types/permission";
import { DeniedRequestStatuses } from "@p0security/shared/types/request-status";
import { Empty, Flex, List, Skeleton, Statistic } from "antd";
import { GraphTooltip } from "components/GraphTooltip";
import { includes, mean, partition, sortBy } from "lodash";
import pluralize from "pluralize";
import React, { useCallback, useMemo } from "react";
import { colors } from "styles/variables";

import {
  DurationFormat,
  GrantToRequestorRecord,
  JitAggregateData,
  ProcessedRequestData,
} from "../../types";

/** Returns true if the change (delta) for the given metric is considered "risky." */
const isRisky = (key: keyof JitAggregateData, delta: number): boolean => {
  const riskRules: Record<keyof JitAggregateData, (n: number) => boolean> = {
    totalRequests: (n) => n < 0,
    meanTimeToApprove: (n) => n > 0,
    totalApprovedRequests: (n) => n < 0,
    uniqueUsers: (n) => n < 0,
    meanDurationOfAccess: (n) => n > 0,
    instantApprovals: (n) => n < 0,
    accessOverThreshold: (n) => n < 0,
    medianDurationOfAccess: () => false, // not used
  };

  return riskRules[key] ? riskRules[key](delta) : false;
};

/** Calculates a percentage value, handling division by zero cases */
const calculatePercentage = (
  numerator: number,
  denominator: number
): number => {
  if (denominator === 0) return 0;
  return (numerator / denominator) * 100;
};

/** Calculates the absolute change for count statistics. */
const calcAbsoluteChange = (curr: number, prev: number): string => {
  if (Number.isNaN(curr) || Number.isNaN(prev)) {
    return "";
  }
  const diff = curr - prev;
  if (diff === 0) return "";
  const sign = diff >= 0 ? "+" : "-";
  return `${sign}${Math.abs(diff).toFixed()}`;
};

/** Calculates the percentage change between current and previous values. */
const calcPercentageChange = (curr: number, prev: number): string | null => {
  if (prev === 0 || curr === 0 || Number.isNaN(curr) || Number.isNaN(prev)) {
    return null;
  }
  const diff = curr - prev;
  if (diff === 0) return null;
  const pct = calculatePercentage(Math.abs(diff), prev);
  const sign = diff >= 0 ? "+" : "-";
  return `${sign}${formatLargePercentage(pct)}`;
};

/** Formats a duration (in milliseconds) to a human-readable format. */
const formatDuration = (milliseconds = 0): DurationFormat => {
  if (milliseconds <= 0) {
    return { simple: "< 1 second", exact: "0h 0m 0s", value: 0 };
  }

  const units = [
    { label: "day", ms: DAYS },
    { label: "hr", ms: HOURS },
    { label: "min", ms: MINUTES },
    { label: "sec", ms: SECONDS },
  ];

  const largestUnit = units.find(({ ms }) => milliseconds >= ms);
  const count = largestUnit ? Math.floor(milliseconds / largestUnit.ms) : 0;
  const simple = largestUnit
    ? `${count} ${pluralize(largestUnit.label, count)}`
    : "< 1 second";

  const hours = Math.floor(milliseconds / HOURS);
  const minutes = Math.floor((milliseconds % HOURS) / MINUTES);
  const seconds = Math.floor((milliseconds % MINUTES) / SECONDS);

  return {
    simple,
    exact: `${hours}h ${minutes}m ${seconds}s`,
    value: milliseconds,
  };
};

const LONG_REQUEST_THRESHOLD = 30 * DAYS;
const INSTANT_APPROVAL_SOURCES: Omit<
  ApprovalDetails["approvalSource"],
  "slack"
>[] = ["persistent", "pagerduty", "evidence"];

/** Process raw requests into an array of ProcessedRequestData. */
const processRequestData = (
  requests: { data: PermissionRequest }[]
): ProcessedRequestData[] =>
  requests
    .filter(({ data: { status } }) => !status.includes("ERRORED"))
    .map(({ data }) => ({
      durationOfRequest: data.revokedTimestamp
        ? data.revokedTimestamp - data.requestedTimestamp
        : 0,
      timeToApprove: data.approvalDetails?.approvedTimestamp
        ? data.approvalDetails.approvedTimestamp - data.requestedTimestamp
        : 0,
      approved: !includes(DeniedRequestStatuses, data.status),
      isInstantApproval:
        !!data.approvalDetails &&
        INSTANT_APPROVAL_SOURCES.includes(data.approvalDetails.approvalSource),
    }));

/** Splits the requests into two groups: "previous" and "current" based on lookbackDays. */
const processRequestDataForComparison = (
  allRequests: { data: PermissionRequest }[],
  lookbackDays: number
) => {
  const now = Date.now();
  const totalLookbackMs = lookbackDays * DAYS;
  const previousStart = now - 2 * totalLookbackMs;
  const previousEnd = now - totalLookbackMs;

  const [previousRequests, currentRequests] = partition(
    allRequests,
    ({ data }) => {
      const t = data.requestedTimestamp;
      return t >= previousStart && t < previousEnd;
    }
  );

  return {
    previous: processRequestData(previousRequests),
    current: processRequestData(currentRequests),
  };
};

/** Aggregate processed request data into summary statistics. */
const aggregateRequests = (
  requestData: ProcessedRequestData[],
  grantToRequestorMap: Record<string, GrantToRequestorRecord>
): JitAggregateData => {
  const initial = {
    totalRequests: 0,
    totalApprovedRequests: 0,
    approvalTimes: [] as number[],
    durations: [] as number[],
    instantApprovals: 0,
  };

  const result = requestData.reduce(
    (
      acc,
      { approved, timeToApprove, durationOfRequest, isInstantApproval }
    ) => ({
      totalRequests: acc.totalRequests + 1,
      totalApprovedRequests: acc.totalApprovedRequests + (approved ? 1 : 0),
      approvalTimes:
        approved && !isInstantApproval
          ? [...acc.approvalTimes, timeToApprove]
          : acc.approvalTimes,
      durations:
        durationOfRequest > 0
          ? [...acc.durations, durationOfRequest]
          : acc.durations,
      instantApprovals: isInstantApproval
        ? acc.instantApprovals + 1
        : acc.instantApprovals,
    }),
    initial
  );

  const accessOverThreshold = result.durations.filter(
    (d) => d > LONG_REQUEST_THRESHOLD
  ).length;
  const meanTimeToApprove = formatDuration(mean(result.approvalTimes));
  const meanDurationOfAccess = formatDuration(mean(result.durations));
  const sortedDurations = sortBy(result.durations);
  const medianDurationOfAccess = formatDuration(
    sortedDurations[Math.floor(sortedDurations.length / 2)] || 0
  );

  const uniqueUsers = new Set(
    Object.values(grantToRequestorMap).flatMap(({ countsByRequestor }) =>
      Object.keys(countsByRequestor)
    )
  ).size;

  return {
    totalRequests: result.totalRequests,
    totalApprovedRequests: result.totalApprovedRequests,
    meanTimeToApprove,
    meanDurationOfAccess,
    medianDurationOfAccess,
    uniqueUsers,
    instantApprovals: result.instantApprovals,
    accessOverThreshold,
  };
};

const formatLargePercentage = (num: number) => {
  const decimals = 1;
  const suffixes = ["", "K", "M", "B"];
  let value = num;
  let suffixIndex = 0;

  while (value >= 1000 && suffixIndex < suffixes.length - 1) {
    value /= 1000;
    suffixIndex++;
  }

  const formattedValue =
    Number((value * Math.pow(10, decimals)).toFixed(0)) /
    Math.pow(10, decimals);
  return `${formattedValue}${suffixes[suffixIndex]}%`;
};

// Renders a statistic item with its tooltip suffix if needed
const StatisticItem: React.FC<StatisticEntry> = ({
  title,
  render,
  icon,
  formattedChange,
  change,
  isRiskier,
  lookbackDays,
  unit,
  prev,
}) => {
  let itemSuffix = null;
  if (formattedChange) {
    const isPositiveChange = formattedChange.startsWith("+");
    const suffixColor = isRiskier
      ? colors.tagColors.red.text
      : colors.success["60"];
    const ArrowIcon = isPositiveChange ? ArrowUpOutlined : ArrowDownOutlined;
    const ArrowEmoji = isPositiveChange ? "↑" : "↓";
    const formattedTooltipText = `${ArrowEmoji} ${change}  ${
      unit ? `${pluralize(unit, parseInt(change))} ` : ""
    }vs. prev ${lookbackDays} days (${prev})`;
    itemSuffix = (
      <GraphTooltip title={formattedTooltipText}>
        <Flex align="center" style={{ fontSize: "12px", marginLeft: "4px" }}>
          <span style={{ color: suffixColor, fontSize: "12px" }}>
            {formattedChange}
          </span>
          <ArrowIcon
            style={{ color: suffixColor, fontSize: "16px", marginLeft: "4px" }}
          />
        </Flex>
      </GraphTooltip>
    );
  }

  return (
    <Statistic
      style={{ marginBottom: ".5em" }}
      title={title}
      valueRender={render}
      prefix={icon}
      suffix={itemSuffix}
    />
  );
};

interface StatisticEntry {
  icon: React.ReactNode;
  key: string;
  title: string;
  render: () => React.ReactNode;
  formattedChange: string | null;
  change: string;
  isRiskier: boolean;
  lookbackDays: number;
  unit: string | null;
  prev: string;
}

interface JitSummaryStatsProps {
  data: { data: PermissionRequest }[];
  requestorMap: Record<string, GrantToRequestorRecord>;
  isLoading: boolean;
  lookbackDays: number;
}

/**
 * Build statistic entries from aggregated data.
 * Calculates delta and percentage change between current and previous values.
 */
const buildRequestData = (
  curr: JitAggregateData,
  prev: JitAggregateData,
  lookbackDays: number
): StatisticEntry[] => [
  {
    icon: (
      <FieldTimeOutlined
        style={{ paddingRight: "8px", color: colors.neutral["40"] }}
      />
    ),
    key: "meanTimeToApprove",
    title: "Mean time to approve",
    render: () => (
      <GraphTooltip title={curr.meanTimeToApprove.exact}>
        <span>{curr.meanTimeToApprove.simple}</span>
      </GraphTooltip>
    ),
    formattedChange: calcPercentageChange(
      curr.meanTimeToApprove.value,
      prev.meanTimeToApprove.value
    ),
    change: formatDuration(
      Math.abs(curr.meanTimeToApprove.value - prev.meanTimeToApprove.value)
    ).simple,
    isRiskier: isRisky(
      "meanTimeToApprove",
      curr.meanTimeToApprove.value - prev.meanTimeToApprove.value
    ),
    lookbackDays,
    unit: null,
    prev: prev.meanTimeToApprove.simple,
  },
  {
    icon: (
      <CheckCircleOutlined
        style={{ paddingRight: "8px", color: colors.neutral["40"] }}
      />
    ),
    key: "requestsPerDay",
    title: "Requests per day",
    render: () => (
      <span>
        {(curr.totalRequests / lookbackDays).toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}
      </span>
    ),
    formattedChange: calcPercentageChange(
      curr.totalRequests,
      prev.totalRequests
    ),
    change: `${Math.abs(curr.totalRequests - prev.totalRequests).toFixed()}`,
    isRiskier: isRisky(
      "totalRequests",
      curr.totalRequests - prev.totalRequests
    ),
    lookbackDays,
    unit: "request",
    prev: prev.totalRequests.toFixed(),
  },
  {
    icon: (
      <LikeOutlined
        style={{ paddingRight: "8px", color: colors.neutral["40"] }}
      />
    ),
    key: "percentageApproved",
    title: "Requests approved",
    render: () => {
      if (curr.totalRequests === 0) {
        return <span>0%</span>;
      }
      const pctApproved = calculatePercentage(
        curr.totalApprovedRequests,
        curr.totalRequests
      );
      const decimals = pctApproved > 5 ? 0 : 1;
      return (
        <span>
          {pctApproved.toLocaleString(undefined, {
            maximumFractionDigits: decimals,
          })}
          %
        </span>
      );
    },
    formattedChange: (() => {
      const currPct = calculatePercentage(
        curr.totalApprovedRequests,
        curr.totalRequests
      );
      const prevPct = calculatePercentage(
        prev.totalApprovedRequests,
        prev.totalRequests
      );
      return `${calcAbsoluteChange(currPct, prevPct)}%`;
    })(),
    change: (() => {
      const currPct = calculatePercentage(
        curr.totalApprovedRequests,
        curr.totalRequests
      );
      const prevPct = calculatePercentage(
        prev.totalApprovedRequests,
        prev.totalRequests
      );
      return Math.abs(currPct - prevPct).toLocaleString(undefined, {
        maximumFractionDigits: 1,
      });
    })(),
    isRiskier: isRisky(
      "totalApprovedRequests",
      curr.totalApprovedRequests - prev.totalApprovedRequests
    ),
    lookbackDays,
    unit: "pp",
    prev: (() => {
      const prevPct = calculatePercentage(
        prev.totalApprovedRequests,
        prev.totalRequests
      );
      return `${prevPct.toLocaleString(undefined, {
        maximumFractionDigits: 1,
      })}%`;
    })(),
  },
  {
    icon: (
      <ClockCircleOutlined
        style={{ paddingRight: "8px", color: colors.neutral["40"] }}
      />
    ),
    key: "instantApprovals",
    title: "Requests automatically approved",
    render: () => {
      if (curr.totalRequests === 0) {
        return <span>0%</span>;
      }
      const pctInstantApprovals = calculatePercentage(
        curr.instantApprovals,
        curr.totalRequests
      );
      const decimals = pctInstantApprovals > 5 ? 0 : 1;
      return (
        <span>
          {pctInstantApprovals.toLocaleString(undefined, {
            maximumFractionDigits: decimals,
          })}
          %
        </span>
      );
    },
    formattedChange: (() => {
      const currPct = calculatePercentage(
        curr.instantApprovals,
        curr.totalRequests
      );
      const prevPct = calculatePercentage(
        prev.instantApprovals,
        prev.totalRequests
      );
      return `${calcAbsoluteChange(currPct, prevPct)}%`;
    })(),
    change: (() => {
      const currPct = calculatePercentage(
        curr.instantApprovals,
        curr.totalRequests
      );
      const prevPct = calculatePercentage(
        prev.instantApprovals,
        prev.totalRequests
      );
      return `${Math.abs(currPct - prevPct).toLocaleString(undefined, {
        maximumFractionDigits: 1,
      })}%`;
    })(),
    isRiskier: isRisky(
      "instantApprovals",
      curr.instantApprovals - prev.instantApprovals
    ),
    lookbackDays,
    unit: "pp",
    prev: (() => {
      const prevPct = calculatePercentage(
        prev.instantApprovals,
        prev.totalRequests
      );
      return `${prevPct.toLocaleString(undefined, {
        maximumFractionDigits: 1,
      })}%`;
    })(),
  },
  {
    icon: (
      <ClockCircleOutlined
        style={{ paddingRight: "8px", color: colors.neutral["40"] }}
      />
    ),
    key: "meanDurationOfAccess",
    title: "Mean duration of access",
    render: () => (
      <GraphTooltip title={curr.medianDurationOfAccess.exact}>
        <span>{curr.medianDurationOfAccess.simple}</span>
      </GraphTooltip>
    ),
    formattedChange: calcPercentageChange(
      curr.medianDurationOfAccess.value,
      prev.medianDurationOfAccess.value
    ),
    change: formatDuration(
      Math.abs(
        curr.medianDurationOfAccess.value - prev.medianDurationOfAccess.value
      )
    ).simple,
    isRiskier: isRisky(
      "meanDurationOfAccess",
      curr.medianDurationOfAccess.value - prev.medianDurationOfAccess.value
    ),
    lookbackDays,
    unit: null,
    prev: prev.meanDurationOfAccess.simple,
  },
  {
    icon: (
      <ClockCircleOutlined
        style={{ paddingRight: "8px", color: colors.neutral["40"] }}
      />
    ),
    key: "accessOverThreshold",
    title: "Access greater than 30 days",
    render: () => <span>{curr.accessOverThreshold}</span>,
    formattedChange: calcPercentageChange(
      curr.accessOverThreshold,
      prev.accessOverThreshold
    ),
    change: Math.abs(
      curr.accessOverThreshold - prev.accessOverThreshold
    ).toFixed(),
    isRiskier: isRisky(
      "accessOverThreshold",
      curr.accessOverThreshold - prev.accessOverThreshold
    ),
    lookbackDays,
    unit: "request",
    prev: prev.accessOverThreshold.toFixed(),
  },
  {
    icon: (
      <UserOutlined
        style={{ paddingRight: "8px", color: colors.neutral["40"] }}
      />
    ),
    key: "uniqueUsers",
    title: "Unique users",
    render: () => <span>{curr.uniqueUsers}</span>,
    formattedChange: calcPercentageChange(curr.uniqueUsers, prev.uniqueUsers),
    change: Math.abs(curr.uniqueUsers - prev.uniqueUsers).toFixed(),
    isRiskier: isRisky("uniqueUsers", curr.uniqueUsers - prev.uniqueUsers),
    lookbackDays,
    unit: "users",
    prev: prev.uniqueUsers.toFixed(),
  },
];

export const JitSummaryStats: React.FC<JitSummaryStatsProps> = ({
  data,
  isLoading,
  requestorMap,
  lookbackDays,
}) => {
  const { current, previous } = useMemo(
    () => processRequestDataForComparison(data, lookbackDays),
    [data, lookbackDays]
  );

  const currentAggregatedRequests = useMemo(
    () => aggregateRequests(current, requestorMap),
    [current, requestorMap]
  );

  const previousAggregatedRequests = useMemo(
    () => aggregateRequests(previous, requestorMap),
    [previous, requestorMap]
  );

  const renderItem = useCallback(
    (entry: StatisticEntry) => <StatisticItem {...entry} />,
    []
  );

  if (isLoading) return <Skeleton active />;

  if (currentAggregatedRequests.totalRequests === 0) {
    return (
      <Empty
        description={`No requests found for the past ${lookbackDays} days.`}
      />
    );
  }

  const requestData = buildRequestData(
    currentAggregatedRequests,
    previousAggregatedRequests,
    lookbackDays
  );

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        justifyContent: "space-around",
        alignItems: "start",
      }}
    >
      <List
        itemLayout="horizontal"
        dataSource={requestData.slice(0, 4)}
        renderItem={renderItem}
      />
      <List
        itemLayout="horizontal"
        dataSource={requestData.slice(4)}
        renderItem={renderItem}
      />
    </div>
  );
};
